import { CircularProgressProps, Link } from '@heroui/react';
import { ReactElement } from 'react';

export const getStatusColor = (status?: string | null) => {
    const statusLower = status?.toLowerCase();
    if (!statusLower) return 'default';

    if (statusLower.includes('live')) {
        return 'success';
    } else if (statusLower.includes('converted to llp') || statusLower.includes('to be ceased') || statusLower.includes('winding up')) {
        return 'warning';
    }
    return 'danger';
};

export function getCircularProgressProps(value: number): CircularProgressProps {
    const color = getColor(value, 'name') as 'success' | 'warning' | 'danger' | 'default' | 'primary' | 'secondary' | undefined;
    const indicator = getColor(value, 'name');

    return {
        color,
        classNames: {
            svg: 'w-52 h-52 drop-shadow-md',
            value: 'text-3xl font-semibold text-black',
            track: 'stroke-zinc-200',
            indicator: indicator !== 'default' ? `!stroke-${indicator}` : '',
        },
        formatOptions: {
            style: 'decimal',
        },
    };
}

export function getTrustDetails(score: number) {
    let category: string | undefined;
    let color: string;
    let description: string;
    let trustLevelText: string;
    let toolTipText: string | ReactElement | undefined;

    if (score >= 90 && score <= 100) {
        category = 'High Trust';
        color = 'success';
        description =
            'Businesses in this category exhibit exceptional reliability and credibility. They are highly trusted within their respective industries and provide a secure environment for transactions and engagements.';
        trustLevelText = 'Trust level is highly likely to be safe';
    } else if (score >= 71 && score <= 89) {
        category = 'Low Risk';
        color = 'success';
        description =
            "Businesses in this range are generally considered reliable. However, it's still advisable to conduct reasonable checks before engaging in transactions.";
        trustLevelText = 'Generally trustworthy, but minor verification is advised.';
    } else if (score >= 31 && score <= 70) {
        // category = 'Preliminary Finding';
        color = 'primary';
        description =
            'Our system is actively collecting and analyzing information about this business. Additional data points are being verified and processed.';
        trustLevelText =
            'Additional information is still being gathered to provide a more comprehensive assessment. We encourage verifying details during this period.';
        toolTipText = (
            <>
                This score may be updated as more data becomes available.{' '}
                <Link href={process.env.NEXT_PUBLIC_DASHBOARD_URL} className="text-blue-600 hover:underline">
                    Business owners
                </Link>{' '}
                can provide relevant information to ensure accuracy and completeness.
            </>
        );
    } else {
        category = 'High Risk';
        color = 'danger';
        description =
            'Businesses in this range may exhibit characteristics that suggest caution. Exercise thorough due diligence before engaging in any transactions.';
        trustLevelText = 'Trust level is low, exercise extreme caution when interacting with this company.';
    }
    // else {
    //     category = 'Neutral';
    //     color = 'default';
    //     description = 'This individual has a neutral trust rating.';
    //     trustLevelText = 'Neutral trust; consider gathering more details.';
    // }

    return {
        category,
        color,
        description,
        trustLevelText,
        toolTipText,
    };
}

export function getColor(score: number, format: 'name' | 'hex' | 'className' = 'name') {
    if (format === 'hex') {
        if (score >= 71 && score <= 100) {
            return '#28a745'; // success/green color in hex
        } else if (score >= 31 && score <= 70) {
            return '#0d6efd'; // primary/blue color in hex
        } else if (score >= 0 && score <= 30) {
            return '#dc3545'; // danger color in hex
        } else {
            return '#6c757d'; // default color in hex
        }
    } else if (format === 'className') {
        if (score >= 71 && score <= 100) {
            return 'bg-success';
        } else if (score >= 31 && score <= 70) {
            return 'bg-primary text-white'; // primary/blue color
        } else if (score >= 0 && score <= 30) {
            return 'bg-danger';
        } else {
            return 'bg-default';
        }
    } else {
        if (score >= 71 && score <= 100) {
            return 'primary'; // primary/blue color
        } else if (score >= 31 && score <= 70) {
            return 'success';
        } else if (score >= 0 && score <= 30) {
            return 'danger';
        } else {
            return 'default';
        }
    }
}
